import {
  type AgentThreadMessage,
  AgentThreadMessageStatus,
  AgentThreadRole,
  AgentThreadStatus,
  type AgentThreadToolResultContent,
  type AgentThreadToolUseContent,
  isAgentThreadToolResultContent,
  isAgentThreadToolUseContent,
} from "@/shared/domain/agentThread/AgentThreadRecord";
import { isClientToolName } from "@/shared/domain/agentThread/clientTools/ClientTools";

/**
 * Where a tool call stands: `pending` (made but not started), `running`, or
 * `done` / `error` once its result arrives.
 */
export type ToolCallStatus = "pending" | "running" | "done" | "error";

/** An unanswered tool call of the turn in flight. */
export interface LiveToolCall {
  toolUse: AgentThreadToolUseContent;
  status: Extract<ToolCallStatus, "pending" | "running">;
}

/** A tool result's outcome: the backend writes `"error"` for a failed call. */
export function toolResultStatus(
  toolResult: AgentThreadToolResultContent,
): Extract<ToolCallStatus, "done" | "error"> {
  return toolResult.status === "error" ? "error" : "done";
}

/** Whether the browser runs this call: a client tool not `answered_by_server`. */
function runsInTheBrowser(toolUse: AgentThreadToolUseContent): boolean {
  return isClientToolName(toolUse.tool_name) && !toolUse.answered_by_server;
}

/**
 * The latest exchange's unanswered tool calls, in order, each with its status.
 *
 * The backend runs calls sequentially only after the assistant message is
 * finalized, so: nothing runs while it is `GENERATING`; on the agent's turn the
 * oldest unanswered server call is running; client tools run on the client
 * tool turn; on any other status calls show as pending.
 */
export function findLiveToolCalls(
  messages: AgentThreadMessage[],
  threadStatus: AgentThreadStatus,
): LiveToolCall[] {
  const lastMessage = messages.at(-1);
  let toolUseMessage: AgentThreadMessage | undefined;
  let answeredToolUseIds = new Set<string>();

  if (lastMessage?.role === AgentThreadRole.ASSISTANT) {
    toolUseMessage = lastMessage;
  } else if (
    lastMessage?.role === AgentThreadRole.ROBOTO &&
    lastMessage.status === AgentThreadMessageStatus.GENERATING &&
    messages.at(-2)?.role === AgentThreadRole.ASSISTANT
  ) {
    toolUseMessage = messages.at(-2);
    answeredToolUseIds = new Set(
      lastMessage.content
        .filter(isAgentThreadToolResultContent)
        .map((content) => content.tool_use_id),
    );
  }

  if (toolUseMessage === undefined) {
    return [];
  }

  const unanswered = toolUseMessage.content
    .filter(isAgentThreadToolUseContent)
    .filter((toolUse) => !answeredToolUseIds.has(toolUse.tool_use_id));

  const callsHaveStarted =
    toolUseMessage.status !== AgentThreadMessageStatus.GENERATING;
  const runningServerCall =
    callsHaveStarted && threadStatus === AgentThreadStatus.ROBOTO_TURN
      ? unanswered.find((toolUse) => !runsInTheBrowser(toolUse))
      : undefined;
  const browserIsRunning =
    callsHaveStarted && threadStatus === AgentThreadStatus.CLIENT_TOOL_TURN;

  return unanswered.map((toolUse) => ({
    toolUse,
    status:
      toolUse === runningServerCall ||
      (browserIsRunning && runsInTheBrowser(toolUse))
        ? "running"
        : "pending",
  }));
}
